import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { Info } from "lucide-react";
import { Close as CloseIcon, StorefrontOutlined, ContentCopy, Check, ManageSearchOutlined } from "@mui/icons-material";
import TenantAutocomplete from "./TenantAutocomplete";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import axiosInstanceImporter from "../axiosInstanceImporter";
import axiosInstance from "../axiosInstance";
import { getRememberedTenantId, rememberTenantId } from "../utils/tenantSelectionCookie";
import LogAgents from "./LogAgents";
import AssetPickerDialog from "./AssetPickerDialog";
import ProductPickerDialog from "./ProductPickerDialog";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InputAdornment from "@mui/material/InputAdornment";

const ImporterTestsTab = ({ apiKey, tenants = [], selectedTenantId = "", setSelectedTenantId, onOpenOverview }) => {
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(false);
  const [testResponse, setTestResponse] = useState(null);
  const [responseTab, setResponseTab] = useState(0);
  const [testTraceId, setTestTraceId] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [feedUrl, setFeedUrl] = useState("");
  const [dataFeedType, setDataFeedType] = useState("");
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  
  const [selectedLogTraceId, setSelectedLogTraceId] = useState("");
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [fieldPopup, setFieldPopup] = useState(null);
  const [variantPopup, setVariantPopup] = useState(null);
  const [viewTab, setViewTab] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchTenantData = async (tenantId) => {
      try {
        const response = await axiosInstance.get(`/tenant/${tenantId}`, {
          headers: { "t-api-key": apiKey },
        });
        if (response.data) {
          if (response.data.dataFeedConnector?.configurations?.dataFeedUrl) {
            setFeedUrl(response.data.dataFeedConnector.configurations.dataFeedUrl);
          }
          if (response.data.dataFeedConnector?.configurations?.dataFeed) {
            setDataFeedType(response.data.dataFeedConnector.configurations.dataFeed);
          }
        }
      } catch (error) {
        console.error("Failed to fetch tenant details:", error);
      }
    };

    const rememberedTenantId = selectedTenantId || getRememberedTenantId();
    if (!rememberedTenantId || tenants.length === 0) return;

    const matchedTenant = tenants.find((tenant) => tenant.id === rememberedTenantId);
    if (matchedTenant && matchedTenant.id !== selectedTenant?.id) {
      setSelectedTenant(matchedTenant);
      
      const directFeedUrl = matchedTenant.dataFeedConnector?.configurations?.dataFeedUrl;
      const directFeedType = matchedTenant.dataFeedConnector?.configurations?.dataFeed;
      
      if (directFeedType) setDataFeedType(directFeedType);
      
      if (directFeedUrl) {
        setFeedUrl(directFeedUrl);
      } else {
        fetchTenantData(matchedTenant.id);
      }
    } else if (matchedTenant && matchedTenant.id === selectedTenant?.id && !feedUrl) {
      const directFeedUrl = matchedTenant.dataFeedConnector?.configurations?.dataFeedUrl;
      if (!directFeedUrl) {
        fetchTenantData(matchedTenant.id);
      }
    }
  }, [tenants, selectedTenantId, selectedTenant, apiKey]);

  const handleTenantChange = async (event, newValue) => {
    setSelectedTenant(newValue);
    rememberTenantId(newValue?.id || "");
    if (typeof setSelectedTenantId === "function") {
      setSelectedTenantId(newValue?.id || "");
    }
    setProductId("");
    setTestResponse(null);
    
    let nextFeedUrl = newValue?.dataFeedConnector?.configurations?.dataFeedUrl || "";
    let nextFeedType = newValue?.dataFeedConnector?.configurations?.dataFeed || "";
    
    setFeedUrl(nextFeedUrl);
    setDataFeedType(nextFeedType);

    if (newValue?.id && (!nextFeedUrl || !nextFeedType)) {
      try {
        const response = await axiosInstance.get(`/tenant/${newValue.id}`, {
          headers: { "t-api-key": apiKey },
        });
        if (response.data) {
          if (response.data.dataFeedConnector?.configurations?.dataFeedUrl) {
            setFeedUrl(response.data.dataFeedConnector.configurations.dataFeedUrl);
          }
          if (response.data.dataFeedConnector?.configurations?.dataFeed) {
            setDataFeedType(response.data.dataFeedConnector.configurations.dataFeed);
          }
        }
      } catch (error) {
        console.error("Failed to fetch tenant details on change:", error);
      }
    }
  };

  const handleTestProductMapping = async () => {
    if (!selectedTenant || !productId) {
      alert("Please select a tenant and provide a product ID.");
      return;
    }

      setLoading(true);
      setTestResponse(null);
      setTestTraceId("");
      setErrorMessage(null);
      setSelectedImageIdx(0);
      setViewTab(0);
      setVariantPopup(null);

    try {
      const response = await axiosInstanceImporter.post(
        "/products/test-ai-product-mapper",
        {
          tenantId: selectedTenant.id,
          productId: productId,
          forceSkipScrape: true,
          FeedUrl: feedUrl,
        },
        {
          headers: { "t-api-key": apiKey },
        }
      );
      const data = response.data;
      
      let tId = 
        data?.traceId || 
        data?.TraceId || 
        data?.traceparent || 
        response.headers?.["traceparent"] || 
        response.headers?.["trace-id"] || 
        response.headers?.["x-trace-id"] || 
        response.headers?.["X-Trace-Id"] || 
        "";
        
      if (!tId && data) {
         tId = data.data?.traceId || data.data?.TraceId || data.data?.traceparent || "";
      }
      
      setTestTraceId(tId || "Not found (Check raw response)");

      if (data?.status >= 400 && data?.message) {
        setErrorMessage(data.message);
      } else {
        setTestResponse(data);
      }
    } catch (error) {
      console.error("Error testing product mappings:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.title ||
        error.message ||
        "Failed to test product mappings.";
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const isShopify = dataFeedType?.toLowerCase() === "shopify" || !!selectedTenant?.shopifyDomain;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3.25,
          borderRadius: "24px",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          backgroundColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
        }}
      >
        <Box display="flex" gap={1.5} mb={1.5} alignItems="center" flexWrap="wrap">
          <TenantAutocomplete
            tenants={tenants}
            value={selectedTenant}
            onChange={handleTenantChange}
            label="Select Tenant"
            sx={{ flex: 1.5, minWidth: 250 }}
            size="small"
          />

          <Tooltip title="Tenant Overview">
            <span>
              <IconButton
                onClick={onOpenOverview}
                disabled={!selectedTenant}
                size="small"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "16px",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  backgroundColor: "#fff",
                  boxShadow: "0 14px 30px rgba(236, 123, 55, 0.08)",
                  color: "#4b5563",
                  "&:hover": {
                    backgroundColor: "#fff7f1",
                    color: "#f97316",
                  },
                }}
              >
                <Info size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <TextField
            label="Product ID"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={!selectedTenant}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Search products">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setProductPickerOpen(true)}
                        disabled={!selectedTenant}
                        sx={{ color: "#f97316", "&:hover": { backgroundColor: "rgba(255,247,241,0.9)" } }}
                      >
                        <ManageSearchOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 2,
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                minHeight: 48,
                borderRadius: "14px",
                backgroundColor: "#fff",
                "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
              },
            }}
          />
        </Box>

        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
          {!isShopify && (
            <TextField
              label="Feed URL"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              disabled={!selectedTenant}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Pick from assets">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setAssetPickerOpen(true)}
                          disabled={!selectedTenant}
                          sx={{ color: "#f97316", "&:hover": { backgroundColor: "rgba(255,247,241,0.9)" } }}
                        >
                          <FolderOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  minHeight: 48,
                  borderRadius: "14px",
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                  "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                  "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                },
              }}
            />
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleTestProductMapping}
            disabled={loading || !selectedTenant || !productId}
            sx={{
              minHeight: 48,
              px: 3,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.85rem",
              color: "#fff",
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
              "&:hover": {
                background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)",
                boxShadow: "0 18px 36px rgba(255, 139, 71, 0.34)",
              },
            }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="inherit" />
                <span>Testing...</span>
              </Box>
            ) : (
              "Test Mappings"
            )}
          </Button>
        </Box>
      </Paper>

      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{
            borderRadius: "16px",
            fontWeight: 500,
          }}
        >
          {errorMessage}
        </Alert>
      )}

      {testResponse && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "22px",
            border: "1px solid rgba(255, 132, 76, 0.10)",
            boxShadow: "0 24px 60px rgba(236, 123, 55, 0.08)",
            backgroundColor: "rgba(255,255,255,0.92)",
          }}
        >
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531" }}>
                  Test Response
                </Typography>
                {testTraceId && (
                  <Tooltip title="Click to view trace details">
                    <Typography
                      variant="body2"
                      onClick={() => {
                        setSelectedLogTraceId(testTraceId);
                        setLogModalOpen(true);
                      }}
                      sx={{
                        mt: 0.5,
                        cursor: "pointer",
                        color: "#f97316",
                        fontWeight: 600,
                        textDecoration: "underline",
                        display: "inline-block",
                        "&:hover": { color: "#ea580c" },
                      }}
                    >
                      Trace ID: {testTraceId}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              <Tabs
                value={responseTab}
                onChange={(_, v) => setResponseTab(v)}
                sx={{
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "999px",
                    background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
                  },
                  "& .MuiTab-root": {
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    py: 1,
                    px: 2,
                    minHeight: 40,
                    borderRadius: "14px",
                    color: "#4b5563",
                    "&.Mui-selected": { color: "#f97316" },
                    "&:hover": { color: "#f97316", backgroundColor: "rgba(255, 247, 241, 0.72)" },
                  }
                }}
              >
                <Tab label="Json Response" />
                <Tab label="Standard View" />
              </Tabs>
            </Box>
          </Box>

          {responseTab === 0 && (
            <Box sx={{ position: "relative" }}>
              <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(testResponse, null, 2));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 1,
                    backgroundColor: "rgba(255,250,247,0.9)",
                    border: "1px solid rgba(249,115,22,0.20)",
                    color: copied ? "#10b981" : "#f97316",
                    "&:hover": { backgroundColor: "rgba(255,247,241,1)", borderColor: "#f97316" },
                  }}
                >
                  {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  borderRadius: "18px",
                  overflow: "hidden",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  boxShadow: "0 16px 34px rgba(236, 123, 55, 0.06)",
                  "& pre": {
                    margin: "0 !important",
                    background: "#fffaf7 !important",
                  },
                }}
              >
                <SyntaxHighlighter
                  language="json"
                  style={docco}
                  customStyle={{ whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0, background: "#fffaf7" }}
                  codeTagProps={{ style: { whiteSpace: "pre-wrap", wordBreak: "break-all" } }}
                >
                  {JSON.stringify(testResponse, null, 2)}
                </SyntaxHighlighter>
              </Box>
            </Box>
          )}

          {responseTab === 1 && (() => {
            const productData = testResponse?.data || testResponse || {};

            const id          = productData.Id          || productData.id          || "";
            const title       = productData.Title       || productData.title       || "";
            const price       = productData.Price       || productData.price       || "";
            const currency    = productData.Currency    || productData.currency    || "";
            const link        = productData.Link        || productData.link        || "";
            const description = productData.Description || productData.description || "";

            const rawImages = productData.Images || productData.images || [];
            const images = rawImages
              .map(img => (typeof img === "string" ? img : img?.src || img?.Src || img?.url || img?.Url || ""))
              .filter(Boolean);

            const variants = (productData.Variants || productData.variants || []).map(v => ({
              sku:          v.Sku          || v.sku          || "",
              title:        v.Title        || v.title        || "",
              price:        v.Price        || v.price        || "",
              stock:        v.StockCount   ?? v.stockCount   ?? v.Stock ?? v.stock ?? "",
              imageUrl:     v.ImageUrl     || v.imageUrl     || "",
              displaySize:  v.DisplaySize  || v.displaySize  || "",
              displayColor: v.DisplayColor || v.displayColor || "",
              link:         v.VariantUrl   || v.variantUrl   || "",
              raw:          v,
            }));

            const variantThumbItems = variants.filter(v => v.imageUrl).map(v => ({ url: v.imageUrl, variantRaw: v.raw }));
            const thumbItems = variantThumbItems.length > 0
              ? variantThumbItems
              : [...new Set(images)].filter(Boolean).map(url => ({ url, variantRaw: null }));
            const allImages = thumbItems.map(t => t.url);
            const currentImageUrl = allImages[selectedImageIdx] || "";

            const knownKeys = new Set([
              "id", "Id", "title", "Title", "price", "Price",
              "link", "Link", "currency", "Currency",
              "description", "Description", "images", "Images",
              "variants", "Variants", "aiFeatures", "AiFeatures",
              "globalAttributes", "GlobalAttributes",
            ]);

            const formatKey = key =>
              key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/^./, s => s.toUpperCase()).trim();

            const stripHtml = html =>
              String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

            const LONG_TEXT_LIMIT = 200;
            const strippedDescription = stripHtml(description);
            const aiFeatures = (productData.aiFeatures || productData.AiFeatures || []).map(f => ({
              name: String(f.name || f.Name || "").replace(/\*\*/g, "").trim(),
              description: String(f.description || f.Description || ""),
            }));

            const rawGlobalAttrs = productData.globalAttributes || productData.GlobalAttributes || null;
            const globalAttrs = rawGlobalAttrs && typeof rawGlobalAttrs === "object" && !Array.isArray(rawGlobalAttrs)
              ? Object.entries(rawGlobalAttrs).filter(([, v]) => v !== null && v !== undefined)
              : [];

            const detailFields = [];
            const tagFields    = [];

            for (const [k, v] of Object.entries(productData)) {
              if (knownKeys.has(k) || v === null || v === undefined || k.startsWith("_")) continue;
              if (Array.isArray(v)) {
                if (v.length > 0 && v.every(item => typeof item !== "object")) {
                  tagFields.push({ key: k, label: formatKey(k), values: v });
                }
              } else if (typeof v !== "object") {
                const raw = String(v);
                detailFields.push({ key: k, label: formatKey(k), value: raw.includes("<") ? stripHtml(raw) : raw });
              }
            }

            const CARD_SX = { borderRadius: "10px", border: "1px solid #e2e5ea", background: "#fff", boxShadow: "none" };
            const SECTION_LABEL_SX = { fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 1.25 };
            const totalStock = variants.reduce((s, v) => s + (Number(v.stock) || 0), 0);

            const attrRow = (rowKey, label, value, isLink, onExpand) => (
              <Box component="tr" key={rowKey} sx={{ "& td": { py: "6px", borderBottom: "1px solid #f0f2f5", verticalAlign: "top" }, "&:last-child td": { borderBottom: "none" } }}>
                <Box component="td" sx={{ color: "#8a94a6", fontSize: "0.75rem", width: 140, pr: 1 }}>{label}</Box>
                <Box component="td" sx={{ color: "#d1d5db", fontSize: "0.75rem", px: 0.5, width: 10 }}>:</Box>
                <Box component="td" sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#1a1a2e", wordBreak: "break-word" }}>
                  {isLink ? (
                    <Typography component="a" href={value} target="_blank" rel="noopener noreferrer"
                      sx={{ color: "#f97316", textDecoration: "none", fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
                      {value.length > 50 ? value.slice(0, 50) + "…" : value} ↗
                    </Typography>
                  ) : onExpand && value.length > LONG_TEXT_LIMIT ? (
                    <>
                      {value.slice(0, LONG_TEXT_LIMIT) + "…"}
                      <Typography component="span" onClick={onExpand}
                        sx={{ color: "#f97316", fontWeight: 600, cursor: "pointer", ml: 0.5, fontSize: "0.72rem", "&:hover": { textDecoration: "underline" } }}>
                        See more →
                      </Typography>
                    </>
                  ) : value || "—"}
                </Box>
              </Box>
            );

            return (
              <>
                <Dialog
                  open={!!fieldPopup}
                  onClose={() => setFieldPopup(null)}
                  maxWidth="md"
                  fullWidth
                  PaperProps={{ sx: { borderRadius: "20px", boxShadow: "0 32px 80px rgba(0,0,0,0.18)" } }}
                >
                  <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#202531" }}>
                      {fieldPopup?.label}
                    </Typography>
                    <IconButton onClick={() => setFieldPopup(null)} size="small" sx={{ color: "#6b7280" }}>
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent dividers sx={{ p: 3 }}>
                    <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.8, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {fieldPopup?.content}
                    </Typography>
                  </DialogContent>
                </Dialog>

                <Dialog
                  open={!!variantPopup}
                  onClose={() => setVariantPopup(null)}
                  maxWidth="md"
                  fullWidth
                  disableScrollLock
                  PaperProps={{ sx: { borderRadius: "20px", boxShadow: "0 32px 80px rgba(0,0,0,0.18)" } }}
                >
                  <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#202531" }}>
                      Variant Data
                    </Typography>
                    <IconButton onClick={() => setVariantPopup(null)} size="small" sx={{ color: "#6b7280" }}>
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ "& pre": { margin: "0 !important", background: "#fffaf7 !important" } }}>
                      <SyntaxHighlighter language="json" style={docco}>
                        {JSON.stringify(variantPopup, null, 2)}
                      </SyntaxHighlighter>
                    </Box>
                  </DialogContent>
                </Dialog>

                <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>

                    <Box sx={{ ...CARD_SX, p: 2.5, display: "flex", gap: 2.5 }}>
                      <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: 280 }}>
                          <Box
                            sx={{
                              width: 280,
                              height: 320,
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid #e2e5ea",
                              background: "rgba(255,247,241,0.6)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {currentImageUrl ? (
                              <Box
                                component="img"
                                src={currentImageUrl}
                                alt={title || "Product"}
                                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <StorefrontOutlined sx={{ fontSize: 64, color: "#f97316", opacity: 0.3 }} />
                            )}
                          </Box>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {id && (
                              <Chip
                                label={`ID: ${id}`}
                                size="small"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.7rem",
                                  backgroundColor: "rgba(255,247,241,0.9)",
                                  color: "#f97316",
                                  border: "1px solid rgba(249,115,22,0.25)",
                                  fontWeight: 700,
                                  height: 22,
                                }}
                              />
                            )}
                            {variants.length > 0 && (
                              <Chip
                                label={`${variants.length} variant${variants.length !== 1 ? "s" : ""}`}
                                size="small"
                                sx={{
                                  fontSize: "0.7rem",
                                  backgroundColor: "rgba(249,115,22,0.12)",
                                  color: "#ea580c",
                                  border: "1px solid rgba(249,115,22,0.20)",
                                  fontWeight: 700,
                                  height: 22,
                                }}
                              />
                            )}
                          </Box>

                          <Typography variant="h6" fontWeight={800} sx={{ color: "#1a1a2e", lineHeight: 1.35, wordBreak: "break-word" }}>
                            {title || "—"}
                          </Typography>
                        </Box>

                        {allImages.length > 0 && (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {(thumbItems.length > 5 ? thumbItems.slice(0, 4) : thumbItems).map((item, i) => {
                              const variantId = item.variantRaw
                                ? (item.variantRaw.Id || item.variantRaw.id || item.variantRaw.VariantId || item.variantRaw.variantId || "")
                                : "";
                              return (
                                <Box
                                  key={i}
                                  onClick={() => {
                                    setSelectedImageIdx(i);
                                    if (item.variantRaw) setVariantPopup(item.variantRaw);
                                  }}
                                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, cursor: "pointer", flexShrink: 0 }}
                                >
                                  <Box
                                    component="img"
                                    src={item.url}
                                    alt={String(i + 1)}
                                    sx={{
                                      width: 76,
                                      height: 76,
                                      borderRadius: "6px",
                                      objectFit: "cover",
                                      border: selectedImageIdx === i ? "2px solid #f97316" : "2px solid #e2e5ea",
                                      opacity: selectedImageIdx === i ? 1 : 0.6,
                                      transition: "all 0.15s ease",
                                      "&:hover": { opacity: 1, borderColor: "#f97316" },
                                    }}
                                  />
                                  {variantId && (
                                    <Typography sx={{ fontSize: "0.58rem", color: "#1a1a2e", fontWeight: 600, fontFamily: "monospace", maxWidth: 76, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                                      {variantId}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                            {thumbItems.length > 5 && (
                              <Box
                                onClick={() => {
                                  const lastIdx = thumbItems.length - 1;
                                  setSelectedImageIdx(lastIdx);
                                  const last = thumbItems[lastIdx];
                                  if (last.variantRaw) setVariantPopup(last.variantRaw);
                                }}
                                sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, cursor: "pointer", flexShrink: 0 }}
                              >
                                <Box
                                  sx={{
                                    width: 76,
                                    height: 76,
                                    borderRadius: "6px",
                                    border: "2px solid #e2e5ea",
                                    background: "rgba(255,247,241,0.8)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.15s ease",
                                    "&:hover": { borderColor: "#f97316", background: "rgba(255,247,241,1)" },
                                  }}
                                >
                                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#f97316" }}>
                                    +{thumbItems.length - 4}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                            <Box component="tbody">
                              {attrRow("price", "Price", price ? `${currency ? currency + " " : ""}${price}` : "—", false, null)}
                              {link && attrRow("link", "Product Link", link, true, null)}
                              {detailFields.map(({ key, label, value }) =>
                                attrRow(key, label, value, false, value.length > LONG_TEXT_LIMIT ? () => setFieldPopup({ label, content: value }) : null)
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ ...CARD_SX }}>
                        <Box sx={{ borderBottom: "1px solid #e2e5ea", px: 2.5, pt: 2 }}>
                          <Tabs
                            value={viewTab}
                            onChange={(_, v) => setViewTab(v)}
                            sx={{
                              minHeight: 36,
                              "& .MuiTabs-indicator": { backgroundColor: "#f97316" },
                              "& .MuiTab-root": { minHeight: 36, fontSize: "0.75rem", fontWeight: 600, textTransform: "none", color: "#9ca3af", px: 1.5, py: 0 },
                              "& .Mui-selected": { color: "#f97316" },
                            }}
                          >
                            <Tab label={`Variants (${variants.length})`} />
                            {aiFeatures.length > 0 && <Tab label="AI Features" />}
                            {globalAttrs.length > 0 && <Tab label="Global Attributes" />}
                          </Tabs>
                        </Box>
                        <Box sx={{ p: 2.5 }}>
                          {(() => {
                            const aiTabIdx = aiFeatures.length > 0 ? 1 : -1;
                            const globalTabIdx = globalAttrs.length > 0 ? (aiFeatures.length > 0 ? 2 : 1) : -1;
                            return (
                              <>
                                {viewTab === 0 && (
                            <>
                              {variants.length > 0 ? (
                                <Box sx={{ overflowX: "auto" }}>
                                  <Box
                                    component="table"
                                    sx={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                      "& th": { fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", py: 1, px: 1.5, borderBottom: "1px solid #e2e5ea", textAlign: "left" },
                                      "& td": { py: 1.25, px: 1.5, borderBottom: "1px solid #f0f2f5", fontSize: "0.78rem", verticalAlign: "middle" },
                                      "& tr:last-child td": { borderBottom: "none" },
                                    }}
                                  >
                                    <Box component="thead">
                                      <Box component="tr">
                                        <Box component="th">SKU</Box>
                                        <Box component="th">Image</Box>
                                        <Box component="th">Size</Box>
                                        <Box component="th">Color</Box>
                                        <Box component="th">Price</Box>
                                        <Box component="th">Stock</Box>
                                        <Box component="th">Link</Box>
                                      </Box>
                                    </Box>
                                    <Box component="tbody">
                                      {variants.map((v, i) => (
                                        <Box component="tr" key={i} sx={{ "&:hover": { background: "rgba(255,247,241,0.5)" } }}>
                                          <Box component="td" sx={{ fontFamily: "monospace", color: "#1a1a2e", fontWeight: 600 }}>
                                            {v.sku || "—"}
                                          </Box>
                                          <Box component="td">
                                            {v.imageUrl ? (
                                              <Box
                                                component="img"
                                                src={v.imageUrl}
                                                alt={v.sku}
                                                sx={{ width: 36, height: 36, borderRadius: "6px", objectFit: "cover", border: "1px solid #e2e5ea" }}
                                              />
                                            ) : (
                                              <Box sx={{ width: 36, height: 36, borderRadius: "6px", background: "rgba(255,247,241,0.8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <StorefrontOutlined sx={{ fontSize: 18, color: "#f97316", opacity: 0.5 }} />
                                              </Box>
                                            )}
                                          </Box>
                                          <Box component="td">
                                            {v.displaySize ? (
                                              <Chip
                                                label={v.displaySize}
                                                size="small"
                                                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, backgroundColor: "rgba(249,115,22,0.10)", color: "#ea580c", border: "none" }}
                                              />
                                            ) : "—"}
                                          </Box>
                                          <Box component="td" sx={{ color: "#374151" }}>{v.displayColor || "—"}</Box>
                                          <Box component="td" sx={{ fontWeight: 700, color: "#f97316" }}>{v.price || "—"}</Box>
                                          <Box component="td">
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                              <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: Number(v.stock) > 0 ? "#10b981" : "#ef4444", flexShrink: 0 }} />
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: Number(v.stock) > 0 ? "#059669" : "#dc2626", fontSize: "0.75rem" }}>
                                                {v.stock !== "" && v.stock !== undefined ? String(v.stock) : "N/A"}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          <Box component="td">
                                            {v.link ? (
                                              <Typography
                                                component="a"
                                                href={v.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: "#f97316", textDecoration: "none", fontSize: "0.75rem", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                                              >
                                                ↗
                                              </Typography>
                                            ) : "—"}
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ color: "#9ca3af" }}>No variants available.</Typography>
                              )}
                            </>
                          )}
                          {viewTab === aiTabIdx && aiTabIdx !== -1 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                              {aiFeatures.map((f, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    py: 1.25,
                                    borderBottom: i < aiFeatures.length - 1 ? "1px solid #f0f2f5" : "none",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#f97316", minWidth: 140, flexShrink: 0 }}>
                                    {f.name}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.78rem", color: "#374151", lineHeight: 1.6 }}>
                                    {f.description}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                          {viewTab === globalTabIdx && globalTabIdx !== -1 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                              {globalAttrs.map(([k, v], i) => (
                                <Box
                                  key={k}
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    py: 1.25,
                                    borderBottom: i < globalAttrs.length - 1 ? "1px solid #f0f2f5" : "none",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#f97316", minWidth: 140, flexShrink: 0 }}>
                                    {formatKey(k)}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.78rem", color: "#374151", lineHeight: 1.6 }}>
                                    {String(v)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </>
                      );
                    })()}
                        </Box>
                      </Box>
                    </Box>

                  <Box sx={{ width: 240, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    {tagFields.length > 0 && (
                      <Box sx={{ ...CARD_SX, p: 2 }}>
                        <Typography sx={{ ...SECTION_LABEL_SX }}>Categories & Tags</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                          {tagFields.map(({ key, label, values }) => (
                            <Box key={key}>
                              <Typography variant="caption" sx={{ color: "#8a94a6", fontSize: "0.68rem", fontWeight: 600, display: "block", mb: 0.5 }}>
                                {label}
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {values.map((val, vi) => (
                                  <Chip
                                    key={vi}
                                    label={String(val)}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      backgroundColor: "rgba(255,247,241,0.9)",
                                      color: "#f97316",
                                      border: "1px solid rgba(249,115,22,0.20)",
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
              );
          })()}
        </Paper>
      )}

      <Dialog
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            m: 0,
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Trace Logs
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setLogModalOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <LogAgents initialTraceId={selectedLogTraceId} />
        </DialogContent>
      </Dialog>

      <AssetPickerDialog
        open={assetPickerOpen}
        onClose={() => setAssetPickerOpen(false)}
        onSelect={(asset) => {
          setFeedUrl(asset.url);
          setAssetPickerOpen(false);
        }}
        tenantId={selectedTenant?.id}
        title="Select a Tenant Asset"
      />

      <ProductPickerDialog
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={(product) => {
          setProductId(product.id);
          setProductPickerOpen(false);
        }}
        tenantId={selectedTenant?.id}
      />
    </>
  );
};

export default ImporterTestsTab;
